import { contactsRetrieveOptions } from '@crm/api'
import { PhoneNumbers } from '@crm/components'
import { useAppNavigate } from '@crm/hooks/useAppNavigate'
import { paths } from '@crm/routes'
import { getCustomFields } from '@crm/utils/getCustomFields'
import { DayLabel } from '@shared/components'
import { useLocales } from '@shared/locales'
import { useQuery } from '@tanstack/react-query'
import { Flex, Modal, Typography } from 'antd'

import {
  Award,
  Briefcase,
  Clock,
  Data,
  HierarchySquare3,
  Link as IconLink,
  Mobile,
  Profile2User,
  ProfileCircle,
  Sms,
  TagUser,
  UserTag
} from 'iconsax-react'
import { Fragment } from 'react/jsx-runtime'
import { useTranslation } from 'react-i18next'

import { ContactDetailsCard } from './ContactDetailsCard'

import type { ModalProps } from 'antd/lib'

const { Link, Paragraph } = Typography

type Props = {
  id?: number
  onCancel?: () => void
} & Omit<ModalProps, 'onCancel'>

export const ContactDetailsModal = ({ id, onCancel, ...props }: Props) => {
  const { currentLang } = useLocales()

  const { data, isLoading } = useQuery({
    ...contactsRetrieveOptions({ path: { id: Number(id) } }),
    enabled: !!id
  })

  const navigate = useAppNavigate()
  const { t } = useTranslation()

  const generalInformation = [
    {
      icon: ProfileCircle,
      label: t('contacts.full_name'),
      text: `${data?.first_name} ${data?.last_name ?? ''}`
    },
    {
      icon: Sms,
      label: t('contacts.email'),
      text: data?.email ? (
        <Link href={`mailto:${data?.email}`}>{data?.email}</Link>
      ) : (
        '-'
      )
    },
    {
      icon: Mobile,
      label: t('contacts.phone_numbers'),
      text: (
        <PhoneNumbers
          onClick={(e) => e.stopPropagation()}
          phoneNumbers={data?.phone_numbers}
        />
      )
    },
    {
      icon: Briefcase,
      label: t('contacts.job_title'),
      text: data?.job_title
    },
    {
      icon: Award,
      label: t('contacts.job_position'),
      text: data?.job_position?.title
    },
    {
      icon: Profile2User,
      label: t('contacts.senior_position'),
      text: data?.job_position?.senior_position?.title
    }
  ]

  const additionInformation = [
    {
      icon: IconLink,
      label: t('contacts.linkedin'),
      text: !!data?.linkedin && (
        <Link href={data?.linkedin}>{t('buttons.open_link')}</Link>
      )
    },
    {
      icon: TagUser,
      label: t('contacts.lead_owner'),
      text:
        (data?.leads?.length ?? 0) > 0
          ? data?.leads.map(({ id, name }, index) => (
              <Fragment key={id}>
                <Link onClick={() => navigate(`${paths.leads}/${id}`)}>
                  {name ?? t('contacts.lead').toLowerCase()}
                </Link>
                {index < (data?.leads?.length ?? 0) - 1 && ', '}
              </Fragment>
            ))
          : '-'
    },
    {
      icon: UserTag,
      label: t('contacts.opportunity'),
      text:
        (data?.opportunities?.length ?? 0) > 0
          ? data?.opportunities.map(({ id, name }) => (
              <Link
                onClick={() =>
                  navigate(
                    `${paths.opportunities}/${data?.opportunity}/details/${id}`
                  )
                }
              >
                {name}
              </Link>
            ))
          : '-'
    },
    {
      icon: Data,
      label: t('contacts.additional_data'),
      text: (
        <Paragraph ellipsis={{ rows: 1, expandable: 'collapsible' }}>
          {data?.additional_data ? String(data?.additional_data) : '-'}
        </Paragraph>
      )
    },
    {
      icon: Clock,
      label: t('lead.created_at'),
      text: <DayLabel date={data?.created_at} />
    },
    {
      icon: Clock,
      label: t('lead.modified_at'),
      text: <DayLabel date={data?.modified_at} />
    }
  ]

  const customFields = getCustomFields(
    data?.custom_field_values,
    t,
    currentLang
  )

  return (
    <Modal
      title={t('contacts.contact_details')}
      onCancel={onCancel}
      okButtonProps={{ hidden: true }}
      width={850}
      open={props.open}
      maskClosable={false}
      loading={isLoading}
      centered
      {...props}
    >
      <Flex vertical gap="middle">
        <Flex gap="middle">
          <ContactDetailsCard key={`general-${id}`} info={generalInformation} />

          <ContactDetailsCard
            key={`additional-${id}`}
            titleKey="contacts.additional_data"
            info={additionInformation}
          />
        </Flex>

        <ContactDetailsCard
          key={`custom-fields-${id}`}
          titleKey="nomenclature.custom_fields"
          info={
            customFields?.map(({ label, children }) => ({
              icon: HierarchySquare3,
              label,
              text: children
            })) ?? []
          }
        />
      </Flex>
    </Modal>
  )
}
